import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import CategoryFilters from 'components/CategoryFilters';
import CategoryFiltersSmall from 'components/CategoryFiltersSmall';
import DiscountBundleBuilder from 'components/DiscountBundleBuilder';
import DiscountSavingsTracker from 'components/DiscountSavingsTracker';
import EmailCapture from 'components/EmailCapture';
import LoadingIndicator from 'components/LoadingIndicator';
import NoItemsFoundContent from 'components/NoItemsFoundContent';
import ProductSortController from 'components/ProductSortController';
import ShopCardList from 'components/ShopCardList';
import SurfBanner from 'components/Surfside/SurfBanner';
import { DISCOUNT_TYPES } from 'constants/discount';
import styles from './CategoryList.module.scss';

const DEFAULT = 'default';

export default function CategoryList({
  backInStockText,
  badgeImages = [],
  category = '',
  closeoutText,
  cmsEmail,
  defaultCategoryImages = [],
  discount,
  facets = [],
  featuredProductText,
  filtersImage,
  groupedDiscountsContent,
  packagedDateTooltipInfo,
  hideSort = false,
  heading,
  location = '',
  newProductText,
  noItemsContent = {},
  path,
  placeholder,
  packagedDateConfiguration,
  priceDropText,
  products: propProducts,
  promotion,
  promotionTiles = [],
  shoppingOptionsType,
  sorts,
  sourceId,
  sourceName,
  subheading,
  onSubheadingClick,
  terpeneTooltipInfo,
  total,
}) {
  const router = useRouter();
  const { filters: filterParam, search: searchParam } = router.query;
  const [loadingStatus, setLoadingStatus] = useState('');
  const [products, setProducts] = useState(propProducts);
  const productsLoading = useSelector((store) => store.productFetchStatus);
  const showSurfsideAds = useSelector(
    (state) => state.featureFlags.surfside_ads
  );
  const topRef = useRef();
  useEffect(() => {
    if (productsLoading !== loadingStatus) {
      setLoadingStatus(productsLoading);
    }
  }, [productsLoading]);

  useEffect(() => {
    if (loadingStatus === 'LOADING') {
      topRef?.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [loadingStatus]);

  useEffect(() => {
    if (propProducts !== products) {
      setProducts(propProducts);
    }
  }, [propProducts]);

  const sortOptions = useMemo(() => {
    if (category === 'flower') {
      return sorts.sort_all_options;
    }
    if (
      category === 'edibles' ||
      category === 'vaporizers' ||
      category === 'concentrates' ||
      category === 'pre-rolls'
    ) {
      return sorts.sort_all_options.filter(
        (option) => option.value !== 'terp_high_to_low'
      );
    }
    return sorts.sort_all_options.filter(
      (option) =>
        option.value !== 'thc_high_to_low' &&
        option.value !== 'thc_low_to_high' &&
        option.value !== 'terp_high_to_low'
    );
  }, [category, sorts]);

  const pagePromotionTiles = promotionTiles
    .filter((tile) => tile.position > 0)
    /* eslint-disable-next-line unicorn/no-array-reduce */
    .reduce((acc, tile) => {
      const {
        position,
        category_effect_store: categoryEffectStore,
        ...rest
      } = tile;
      const sanitizedCategoryEffectStore = (categoryEffectStore || DEFAULT)
        .replace(/\W/g, '')
        .toLowerCase();
      const sanitizedCategory = category.replace(/\W/g, '').toLowerCase();
      const sanitizedLocation = location.replace(/\W/g, '').toLowerCase();

      if (
        sanitizedCategoryEffectStore === DEFAULT ||
        sanitizedCategoryEffectStore === sanitizedCategory ||
        sanitizedCategoryEffectStore === sanitizedLocation
      ) {
        const previous = acc[position];
        if (previous) {
          const { categoryEffectStore: previousCategoryEffectStore } = previous;
          if (
            sanitizedCategoryEffectStore === DEFAULT ||
            previousCategoryEffectStore === sanitizedLocation ||
            (previousCategoryEffectStore === sanitizedCategory &&
              sanitizedCategoryEffectStore === sanitizedLocation)
          ) {
            return acc;
          }
        }

        return {
          ...acc,
          [position]: {
            position,
            categoryEffectStore: sanitizedCategoryEffectStore,
            ...rest,
          },
        };
      }

      return acc;
    }, {});
  const promotionTilesArray = Object.values(pagePromotionTiles) || [];
  const productsLength = products.length;
  const tileList = promotionTilesArray
    .sort((a, b) => a.position - b.position)
    /* eslint-disable-next-line unicorn/no-array-reduce */
    .reduce((acc, tile) => {
      if (tile.position <= productsLength) {
        const clone = [...acc];
        clone.splice(tile.position - 1, 0, tile);
        return clone;
      }

      return acc;
    }, products);

  const hasFacetSelected = (filterParam || '').split(',').some(Boolean);
  const hasTiles = tileList.length > 0;
  const showSortController =
    !hideSort && sorts && (hasTiles || hasFacetSelected);
  const productsAreLoading = loadingStatus === 'LOADING';

  const HeaderContainerTag = subheading ? 'button' : 'div';

  if (!location) {
    facets = facets.filter((facet) => facet.facet !== 'Price');
  }

  const { isBundledDiscount, discountType, reward } = discount ?? {};
  const { discountThreshold } = reward ?? {};

  const showBundleBuilder =
    isBundledDiscount ||
    discountType === DISCOUNT_TYPES.bundle ||
    discountThreshold > 0;

  const showSavingsTracker =
    discountType === DISCOUNT_TYPES.mixandmatch && !showBundleBuilder;

  const showFilters = !showBundleBuilder || !discount?.isMultiStep;

  return (
    <>
      <section className={`mx-auto ${styles.wrapper}`}>
        <div className="d-flex flex-column flex-md-row justify-content-between px-8 mb-8 mt-lg-6">
          {heading ? (
            <div className="d-flex text-primary align-items-start mt-6 mb-8">
              <HeaderContainerTag
                className={classNames(
                  'btn-transparent p-0 text-primary',
                  subheading ? styles.heading_and_subheading : styles.heading,
                  subheading && 'btn'
                )}
                onClick={onSubheadingClick}
              >
                <h1
                  ref={topRef}
                  className={classNames(
                    'fs-14 fs-md-17 fs-lg-40',
                    styles.heading
                  )}
                >
                  {heading}
                </h1>
                {subheading && <p className="py-2">{subheading}</p>}
              </HeaderContainerTag>
              {total ? (
                <div className="d-flex flex-column align-items-start h-100">
                  <p
                    className={classNames(
                      styles.totals_text,
                      'font-roboto ps-10 fs-9 mb-0',
                      subheading ? 'mt-2 mt-md-4' : 'mt-2 mt-md-4'
                    )}
                  >
                    {total} Items
                  </p>
                </div>
              ) : null}
            </div>
          ) : (
            <div />
          )}
          {showSortController && (
            <div className="d-flex flex-row justify-content-between mt-md-5">
              <CategoryFiltersSmall
                category={category}
                closeoutText={closeoutText}
                facets={facets}
                filterParam={filterParam}
                filtersImage={filtersImage}
                path={path}
                promotion={promotion}
                searchParam={searchParam}
                title={heading}
              />
              <ProductSortController
                categoryFiltersEnabled
                promotion={promotion}
                sortOptions={sortOptions}
              />
            </div>
          )}
        </div>
        {showBundleBuilder && (
          <DiscountBundleBuilder
            discount={discount}
            products={propProducts}
            setProducts={setProducts}
          />
        )}
        {showSavingsTracker && (
          <DiscountSavingsTracker discount={discount} products={propProducts} />
        )}
        {hasTiles || hasFacetSelected || productsAreLoading ? (
          <div className="d-flex flex-row px-8">
            {showFilters && (
              <CategoryFilters
                category={category}
                closeoutText={closeoutText}
                facets={facets}
                filterParam={filterParam}
                path={path}
                promotion={promotion}
                searchParam={searchParam}
                title={heading}
              />
            )}
            <div
              className={classNames(styles.list_wrapper, {
                [styles.list_wrapper_with_sidebar]: showFilters,
              })}
            >
              <SurfBanner
                className="surf-banner-container"
                showBanner={showSurfsideAds}
                zoneId="i9896"
              />
              {productsAreLoading ? (
                <LoadingIndicator customCss="mt-md-40" />
              ) : hasTiles ? (
                <ShopCardList
                  withSideBarStyle={showFilters}
                  backInStockText={backInStockText}
                  badgeImages={badgeImages}
                  closeoutText={closeoutText}
                  defaultCategoryImages={defaultCategoryImages}
                  featuredProductText={featuredProductText}
                  groupedDiscountsContent={groupedDiscountsContent}
                  packagedDateTooltipInfo={packagedDateTooltipInfo}
                  location={location}
                  newProductText={newProductText}
                  pagePromotionTiles={pagePromotionTiles}
                  placeholder={placeholder}
                  packagedDateConfiguration={packagedDateConfiguration}
                  priceDropText={priceDropText}
                  products={products}
                  sourceId={sourceId}
                  sourceName={sourceName}
                  terpeneTooltipInfo={terpeneTooltipInfo}
                />
              ) : (
                <NoItemsFoundContent
                  facets={facets}
                  filterParam={filterParam}
                  noItemsContent={noItemsContent}
                  optionsType={shoppingOptionsType}
                  path={path}
                />
              )}
            </div>
          </div>
        ) : (
          <NoItemsFoundContent
            facets={facets}
            filterParam={filterParam}
            noItemsContent={noItemsContent}
            optionsType={shoppingOptionsType}
          />
        )}
      </section>
      {cmsEmail?.header && <EmailCapture cmsData={cmsEmail} />}
    </>
  );
}
